import React from 'react';
import { photoHintsOf } from '../lib/storePhotos';
import { Link } from 'react-router-dom';
import PriceDisplay from './PriceDisplay';
import SealedArt from './SealedArt';
import { EXCLUSIVE_LABELS } from '../lib/productTypes';
import { gameShortLabel, gameOfId } from '../lib/games';

// WO-20: one sealed product in a grid (set page, sealed browse, search, product page "more from this set").
// Same frame as CardListItem; the badge says what it is, so mixed result lists stay readable.
export function TypeBadge({ children, tone = 'purple' }) {
  const tones = {
    purple: 'border-purple/50 text-cream/90',
    presale: 'border-yellow-400/60 text-yellow-300',
    sealed: 'border-skyblue/40 text-skyblue',
  };
  return (
    <span className={`inline-flex items-center rounded-full bg-dark/90 border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${tones[tone] || tones.purple}`}>
      {children}
    </span>
  );
}

// WO-26: `showGame` adds the game's name (all-games search mixes games).
export function GameBadge({ game }) {
  return (
    <span className="inline-flex items-center rounded-full bg-dark/90 border border-cream/30 px-2 py-0.5 text-[11px] font-medium text-cream/90 whitespace-nowrap">
      {gameShortLabel(game)}
    </span>
  );
}

export default function ProductTile({ product: p, showSet = true, compact = false, onClick = null, showGame = false }) {
  const price = p.tcgplayer_price?.price;
  const exclusive = Array.isArray(p.exclusive) ? p.exclusive : [];
  return (
    <Link
      to={`/product/${encodeURIComponent(p.product_id)}`}
      onClick={onClick || undefined}
      data-testid="product-tile"
      className="group bg-dark border border-purple/20 rounded-lg overflow-hidden hover:border-purple/60 transition-all duration-300 hover:-translate-y-1 flex flex-col"
    >
      <div className="relative">
        <SealedArt productType={p.product_type} typeName={p.type_name} image={p.image_small} alt={p.name}
                   className={compact ? 'aspect-[16/10]' : 'aspect-[4/3]'} showLabel={false}
                   cover={p.cover} setCode={p.set_code} art={p.art_image}
                   photo={p.photo_url} photoCredit={p.photo_credit} photoHints={photoHintsOf(p)} />
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          <TypeBadge tone="sealed">{p.type_name || 'Sealed'}</TypeBadge>
          {p.status === 'presale' && <TypeBadge tone="presale">Pre-release</TypeBadge>}
        </div>
        {showGame && (
          <div className="absolute bottom-2 left-2" data-testid="game-badge">
            <GameBadge game={p.tcg || p.game_id || gameOfId(p.product_id)} />
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-sm font-medium text-cream leading-snug line-clamp-2" title={p.name}>{p.name}</h3>
        <div className="text-xs text-cream/50 mt-1 truncate">
          {showSet && p.set_name ? p.set_name : null}
          {exclusive.length > 0 && `${showSet && p.set_name ? ' · ' : ''}${exclusive.map((x) => EXCLUSIVE_LABELS[x] || x).join(', ')} exclusive`}
        </div>
        <div className="mt-auto pt-2 flex items-baseline justify-between gap-2">
          {price ? (
            <span className="text-green font-semibold"><PriceDisplay amountCad={price} /></span>
          ) : (
            <span className="text-xs text-cream/40">No price yet</span>
          )}
          {price ? <span className="text-[10px] uppercase tracking-wider text-cream/40">TCGplayer</span> : null}
        </div>
      </div>
    </Link>
  );
}

export function ProductGrid({ items, showSet = true, className = '', showGame = false }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 ${className}`}>
      {items.map((p) => <ProductTile key={p.product_id} product={p} showSet={showSet} showGame={showGame} />)}
    </div>
  );
}
